import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Row, Col } from 'react-flexbox-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AiOutlineSetting } from 'react-icons/ai';
import { FaFileExport, FaCogs } from 'react-icons/fa';
import {
  AddCard,
  RetrospectiveCard,
  Container,
  Popup,
  Search,
  LoadingPage,
  Title,
  Dropdown,
} from '../components';
import FormRetrospective from './FormRetrospective';
import {
  getRetrospectives,
  deleteRetrospective,
} from '../services/retrospectives';
import { GlobalContext } from '../context/GlobalState';
import { SCREENS } from '../constants';
import { getUsersById } from '../services/users';
import { errorNoticeDefault } from '../utils/notice';
import { logEvent, TrackingEvents } from '../tracking';
import * as S from './style';
import { Retrospective } from '../@types/domain';
import { ExportRetropectivesPopup } from 'src/components/ExportRetrospectivesPopup';
import { TrialBanner } from 'src/components/TrialBanner';

let searchTermDebounce: any = null;

function useRetrospectives(searchTerm: string) {
  const { onLoading, isLoading } = useContext(GlobalContext);
  return useQuery<Retrospective[], Error, Retrospective[]>(
    ['getRetrospectives', searchTerm],
    () => getRetrospectives(searchTerm),
    {
      enabled: !isLoading,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      onSettled() {
        onLoading(false);
      },
      onSuccess() {
        onLoading(false);
        return (
          searchTerm !== '' && logEvent(TrackingEvents.SEARCH_RETROSPECTIVES)
        );
      },
      onError(error) {
        onLoading(false);
        errorNoticeDefault(error);
      },
    }
  );
}

function useUsers(userIds: string[] | null) {
  return useQuery(
    ['getUsersById', ...(userIds || [])],
    () => getUsersById(userIds),
    {
      enabled: !!userIds,
      onError(error) {
        errorNoticeDefault(error);
      },
    }
  );
}

type DeleteRetrospectivePopupProperties = {
  retrospective: Retrospective;
  onCancel: () => void;
};

function DeleteRetrospectivePopup({
  retrospective,
  onCancel,
}: DeleteRetrospectivePopupProperties) {
  const queryClient = useQueryClient();

  const { onLoading, isLoading } = useContext(GlobalContext);

  const handleConfirmDelete = async () => {
    logEvent(TrackingEvents.DELETE_RETROSPECTIVE);
    onLoading(true);
    try {
      await deleteRetrospective(retrospective.id);
      onCancel();
      await queryClient.invalidateQueries(['getRetrospectives']);
    } catch (error) {
      errorNoticeDefault(error);
    } finally {
      onLoading(false);
    }
  };

  const isFetchingRetrospectives = queryClient.isFetching([
    'getRetrospectives',
  ]);

  return (
    <Popup
      open={!!retrospective}
      title="Delete retrospective"
      body={`Are you sure you want to delete the retrospective "${
        retrospective.name || ''
      }"? This action cannot be undone.`}
      cancelLabel="Cancel"
      onCancel={onCancel}
      confirmLabel="Delete"
      onConfirm={handleConfirmDelete}
      disabledConfirm={isLoading || isFetchingRetrospectives > 0}
    />
  );
}

const IconSettings = () => {
  const { onChangeScreen } = useContext(GlobalContext);
  const [openPopup, setOpenPopup] = useState(false);

  const handleOptionSelected = async ({ value }) => {
    if (value === 'settings') {
      onChangeScreen(SCREENS.SETTINGS);
    } else if (value === 'export') {
      logEvent(TrackingEvents.EXPORT_RETROSPECTIVES);
      setOpenPopup(true);
    }
  };

  return (
    <>
      <Dropdown
        onSelect={handleOptionSelected}
        openRight={true}
        triggerContent={
          <S.Icon>
            <AiOutlineSetting title="Settings" size="1.5em" />
          </S.Icon>
        }
        options={[
          {
            value: 'export',
            label: 'Export retrospectives',
            icon: <FaFileExport />,
          },
          {
            value: 'settings',
            label: 'Settings...',
            icon: <FaCogs />,
          },
        ]}
      ></Dropdown>

      {openPopup && (
        <ExportRetropectivesPopup
          open={openPopup}
          onCancel={() => setOpenPopup(false)}
        ></ExportRetropectivesPopup>
      )}
    </>
  );
};

function Workspace() {
  const { isLoading: isGlobalLoading, onChangeScreen } =
    useContext(GlobalContext);
  const [search, setSearch] = useState('');
  const [searchByDebouce, setSearchbyDebounce] = useState('');
  const [openPopup, setOpenPopup] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: retrospectives,
    isSuccess: isSuccessRetrospectives,
    isLoading: isLoadingRetrospectives,
  } = useRetrospectives(searchByDebouce);

  const userIds = (retrospectives || []).map((r) => r.createdBy);
  const {
    data: users,
    isLoading: isLoadingUsers,
    isSuccess: isSuccessLoadedUsers,
  } = useUsers(isSuccessRetrospectives ? [...new Set(userIds)] : null);

  const isLoading = isLoadingRetrospectives || isLoadingUsers;

  useEffect(() => {
    onChangeScreen(SCREENS.WORKSPACE);
  }, [onChangeScreen]);

  useEffect(() => {
    logEvent(TrackingEvents.VIEW_DASHBOARD);
  }, []);

  const [itemToEdit, setItemToEdit] = useState<Retrospective | null>(null);
  const [itemToDuplicate, setItemToDuplcate] = useState<Retrospective | null>(
    null
  );
  const [retrospectiveToDelete, setRetrospectiveToDelete] =
    useState<Retrospective | null>(null);

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    clearTimeout(searchTermDebounce);
    searchTermDebounce = setTimeout(() => {
      setSearchbyDebounce(searchTerm);
    }, 750);
  }, []);

  const handleRetrospectiveClick = (retrospectiveId: string) => {
    logEvent(TrackingEvents.OPEN_RETROSPECTIVE);
    onChangeScreen(SCREENS.BOARD, retrospectiveId);
  };

  const handleDeleteClick = (retrospective: Retrospective) => {
    setRetrospectiveToDelete(retrospective);
  };

  const handleEditRetrospective = (retrospective: Retrospective) => {
    setItemToEdit(retrospective);
    setOpenPopup(true);
  };

  const onCloseForm = (reload = false) => {
    if (reload) {
      queryClient.invalidateQueries(['getRetrospectives']);
    }
    setItemToEdit(null);
    setItemToDuplcate(null);
    setOpenPopup(false);
  };

  return (
    <Container>
      <TrialBanner />
      <S.WorkspaceToolbar>
        <Title>Retrospectives</Title>

        <S.ToolbarRight>
          <Search search={search} handleSearch={handleSearch} />
          <IconSettings />
        </S.ToolbarRight>
      </S.WorkspaceToolbar>
      {isGlobalLoading && <LoadingPage text="Loading..." />}
      {retrospectives && !isLoading && isSuccessLoadedUsers && (
        <Row>
          <Col xs={3}>
            <AddCard onClick={() => setOpenPopup(true)} />
          </Col>
          {retrospectives.map((retrospective) => {
            return (
              <Col xs={3} key={retrospective.id + retrospective.stage}>
                <RetrospectiveCard
                  users={users}
                  onDuplicate={(event_) => {
                    event_.stopPropagation();
                    setItemToDuplcate(retrospective);
                    setOpenPopup(true);
                  }}
                  retrospective={retrospective}
                  onClick={() => handleRetrospectiveClick(retrospective.id)}
                  onDelete={() => handleDeleteClick(retrospective)}
                  onEdit={handleEditRetrospective}
                />
              </Col>
            );
          })}
        </Row>
      )}

      {retrospectiveToDelete && !isGlobalLoading && (
        <DeleteRetrospectivePopup
          retrospective={retrospectiveToDelete}
          onCancel={() => setRetrospectiveToDelete(null)}
        />
      )}

      {!isGlobalLoading && (
        <FormRetrospective
          key={itemToEdit?.id || 'new-retrospective'}
          open={openPopup}
          onClose={onCloseForm}
          editing={itemToEdit}
          duplicating={itemToDuplicate}
        />
      )}
    </Container>
  );
}

export default Workspace;
